import { AppAuthProvider, ErrorBoundary } from 'auth';
import { Suspense, lazy } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { WelcomePopupController } from '@/modules/auth/components/WelcomePopupController.tsx';
import { getAuthRoutes } from '@/modules/auth/routes.tsx';
import { commonPaths } from '@/modules/common/routes.tsx';
import { BulkReportDownloadController } from '@/modules/common/components/reports/BulkReportDownloadController.tsx';
import { getAdminDashboardRoutes } from '@/modules/admin-dashboard/routes.tsx';
import { getEntityRoutes } from '@/modules/entity/routes.tsx';
import { getInvestorDashboardRoutes } from '@/modules/investor-dashboard/routes.tsx';
import { getIssuerDashboardRoutes } from '@/modules/issuer-dashboard/routes.tsx';
import { ModalProvider, Spinner } from 'ui';
import { NavigateBlockerProvider } from 'ui/hooks';
import RootRoute from '@/RootRoute.tsx';

const NotFound = lazy(() => import('./modules/common/pages/NotFound.tsx'));
const Policy = lazy(() => import('./modules/common/pages/Policy.tsx'));
const AcknowledgementsAndDeclarations = lazy(
  () => import('./modules/common/pages/AcknowledgementsAndDeclarations.tsx'),
);
const CheckShareCertificate = lazy(
  () => import('./modules/common/pages/CheckShareCertificate.tsx'),
);

export const AppRoutes = () => {
  const routerRoutes: RouteObject[] = [
    {
      path: '',
      element: (
        <AppAuthProvider site="sr">
          <ErrorBoundary homeUrl={commonPaths.static.root}>
            <ModalProvider>
              <WelcomePopupController />
              <BulkReportDownloadController />
              <NavigateBlockerProvider>
                <Outlet />
              </NavigateBlockerProvider>
            </ModalProvider>
          </ErrorBoundary>
        </AppAuthProvider>
      ),
      children: [
        {
          path: '/',
          element: <RootRoute />,
        },
        {
          path: '/login',
          element: <RootRoute />,
        },
        ...getAuthRoutes(),
        ...getEntityRoutes(),
        ...getInvestorDashboardRoutes(),
        ...getIssuerDashboardRoutes(),
        ...getAdminDashboardRoutes(),
        {
          path: commonPaths.static.checkShareCertificate,
          element: <CheckShareCertificate />,
        },
        {
          path: commonPaths.static.notFound,
          element: <NotFound />,
        },
        {
          path: commonPaths.static.privacyPolicy,
          element: <Policy />,
        },
        {
          path: commonPaths.static.acknowledgementsAndDeclarations,
          element: <AcknowledgementsAndDeclarations />,
        },
        {
          path: '*',
          element: <Navigate to={commonPaths.static.notFound} replace />,
        },
      ],
    },
  ];

  const router = createBrowserRouter(routerRoutes);

  return (
    <Suspense
      fallback={
        <div className="flex h-[100dvh] w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      }
    >
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
          // v7_relativeSplatPath: true,
        }}
      />
    </Suspense>
  );
};
